import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "gatsby";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Logo from "./Logo";
import Button from "./Button";
import { Menu, Close, Phone } from "./Icons";
import site from "../data/site";
import * as s from "./Nav.module.css";

export const NAV_LINKS = [
  { to: "/loan-programs/", label: "Loan Programs" },
  { to: "/process/", label: "Process" },
  { to: "/calculators/", label: "Calculators" },
  { to: "/reviews/", label: "Reviews" },
  { to: "/for-agents/", label: "For Agents" },
  { to: "/about/", label: "About" },
];

const DESKTOP = "(min-width: 1024px)";

export default function Nav({ path = "/", theme = "dark" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null);
  const panelRef = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close on route change.
  useEffect(() => { setOpen(false); }, [path]);

  // Close if the viewport grows past the mobile breakpoint while open.
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP);
    const onChange = (e) => { if (e.matches) setOpen(false); };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Scroll lock, Escape to close, focus into the panel and back to the toggle.
  useEffect(() => {
    if (!open) return;
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    const first = panelRef.current?.querySelector("a, button");
    first?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key !== "Tab" || !panelRef.current) return;
      const items = panelRef.current.querySelectorAll("a, button");
      if (!items.length) return;
      const firstEl = items[0], lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); toggleRef.current?.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); toggleRef.current?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.documentElement.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
      toggleRef.current?.focus();
    };
  }, [open]);

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const light = theme === "light" && !scrolled && !open;
  const cls = [s.nav, scrolled ? s.scrolled : "", light ? s.light : ""].filter(Boolean).join(" ");
  const isActive = (to) => path === to || (to !== "/" && path.startsWith(to));

  return (
    <>
      <header className={cls}>
        <div className={`container ${s.inner}`}>
          <Logo tone={light ? "light" : "dark"} />
          <nav className={s.links} aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={[s.link, isActive(l.to) ? s.active : ""].filter(Boolean).join(" ")}>{l.label}</Link>
            ))}
          </nav>
          <div className={s.actions}>
            <a href={`tel:${site.contact.phoneRaw}`} className={s.phone}><Phone size={16} />{site.contact.phone}</a>
            <Button to="/contact/" size="sm" variant={light ? "light" : "primary"}>Get pre-approved</Button>
            <button ref={toggleRef} type="button" className={s.burger} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="mobile-menu" onClick={toggle}>
              {open ? <Close size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div id="mobile-menu" ref={panelRef} className={s.panel} role="dialog" aria-modal="true" aria-label="Menu" initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduce ? 0 : 0.25 }}>
            <nav className={`container ${s.panelInner}`} aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.to} initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: reduce ? 0 : 0.05 + i * 0.04, duration: reduce ? 0 : 0.4, ease: [0.16, 1, 0.3, 1] }}>
                  <Link to={l.to} className={[s.panelLink, isActive(l.to) ? s.active : ""].filter(Boolean).join(" ")}>{l.label}</Link>
                </motion.div>
              ))}
              <div className={s.panelActions}>
                <Button to="/contact/" variant="primary" block>Get pre-approved</Button>
                <Button href={`tel:${site.contact.phoneRaw}`} variant="secondary" block><Phone size={16} />{site.contact.phone}</Button>
              </div>
              <p className="small slate">{site.person.name} · NMLS {site.person.nmls}</p>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
